import { useState, useEffect } from 'react';
import { Search, FilterList, Download, Visibility, Edit, Delete } from '@mui/icons-material';
import FilterDropdown from '../common/FilterDropdown';

const CustomerTable = () => {
  const [filters, setFilters] = useState({
    segment: '',
    status: '',
    region: '',
    rmCode: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Simulate customer data based on CSV structure
    const mockCustomers = [
      {
        id: '101136',
        name: 'Khaled Al-Maani',
        segment: 'Elites',
        status: 'Active',
        region: 'Malawi',
        rmCode: 'Saif Tawfiq',
        annualIncome: 979872,
        products: 4,
        totalBalance: 1606369.47,
        lastActivity: '2 days ago'
      },
      {
        id: '101982',
        name: 'Economically Vulnerable',
        segment: 'Priority',
        status: 'Active',
        region: 'Jordan',
        rmCode: 'Saif Tawfiq',
        annualIncome: 969942,
        products: 5,
        totalBalance: 1307689.8,
        lastActivity: '1 day ago'
      },
      {
        id: '102874',
        name: 'Mahmoud Al-Jundi',
        segment: 'Premium',
        status: 'Active',
        region: 'USA',
        rmCode: 'Ahmad Al-Tamimi',
        annualIncome: 862087,
        products: 3,
        totalBalance: 235066.75,
        lastActivity: '3 days ago'
      },
      {
        id: '103926',
        name: 'Amira Al-Kurdi',
        segment: 'General Mass Market',
        status: 'Active',
        region: 'France',
        rmCode: 'Nader Darwish',
        annualIncome: 606109,
        products: 4,
        totalBalance: 197688.31,
        lastActivity: '1 week ago'
      },
      {
        id: '101915',
        name: 'Yousef Al-Zoubi',
        segment: 'Premium',
        status: 'Active',
        region: 'Canada',
        rmCode: 'Layla Shuman',
        annualIncome: 147198,
        products: 2,
        totalBalance: 780424.95,
        lastActivity: '2 days ago'
      }
    ];

    setCustomers(mockCustomers);
    setFilteredCustomers(mockCustomers);
  }, []);

  useEffect(() => {
    let filtered = customers.filter(customer => {
      const matchesSearch = customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           customer.id.includes(searchQuery);
      const matchesSegment = !filters.segment || customer.segment === filters.segment;
      const matchesStatus = !filters.status || customer.status === filters.status;
      const matchesRegion = !filters.region || customer.region === filters.region;
      const matchesRM = !filters.rmCode || customer.rmCode === filters.rmCode;

      return matchesSearch && matchesSegment && matchesStatus && matchesRegion && matchesRM;
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  }, [customers, searchQuery, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      segment: '',
      status: '',
      region: '',
      rmCode: ''
    });
    setSearchQuery('');
  };

  const getSegmentColor = (segment) => {
    switch (segment) {
      case 'Premium':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Priority':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Employee Banking':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      default:
        return 'bg-[#808080] text-[#FFFFFF]';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Dormant':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#B4E50D] text-[#0A0A0A]';
    }
  };

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  return (
    <div className="bg-[#1A1A1A] rounded-xl p-6 shadow-xl border border-[#2A2A2A] hover:border-[#B4E50D] transition-all duration-300">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-[#FFFFFF]">Customer Portfolio</h2>
          <p className="text-[#B0B0B0] mt-1">Manage and monitor your customer relationships</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#FFFFFF] transition-colors">
            <Download />
          </button>
          <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#FFFFFF] transition-colors">
            <FilterList />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-[#B0B0B0]" />
          <input
            type="text"
            placeholder="Search customers by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FilterDropdown
            label="Segment"
            options={['', 'Premium', 'Priority', 'Employee Banking', 'General Mass Market', 'Private']}
            value={filters.segment}
            onChange={(e) => handleFilterChange('segment', e.target.value)}
          />
          <FilterDropdown
            label="Status"
            options={['', 'Active', 'Dormant']}
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          />
          <FilterDropdown
            label="Region"
            options={['', 'Jordan', 'France', 'USA', 'Canada', 'Germany', 'UK', 'India', 'Australia']}
            value={filters.region}
            onChange={(e) => handleFilterChange('region', e.target.value)}
          />
          <FilterDropdown
            label="RM Code"
            options={['', 'Saif Tawfiq', 'Ahmad Al-Tamimi', 'Nader Darwish', 'Layla Shuman']}
            value={filters.rmCode}
            onChange={(e) => handleFilterChange('rmCode', e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-[#B0B0B0]">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredCustomers.length)} of {filteredCustomers.length} customers
          </div>
          <button 
            onClick={resetFilters}
            className="text-sm text-[#B4E50D] hover:text-[#B4E50D] transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#2A2A2A] border-b border-[#3A3A3A]">
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Segment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Region</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">RM</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Products</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Balance</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2A2A]">
            {currentCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-[#2A2A2A] transition-colors">
                <td className="px-4 py-4">
                  <div>
                    <div className="font-medium text-[#FFFFFF]">{customer.name}</div>
                    <div className="text-sm text-[#B0B0B0]">ID: {customer.id}</div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSegmentColor(customer.segment)}`}>
                    {customer.segment}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                    {customer.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-[#FFFFFF]">{customer.region}</td>
                <td className="px-4 py-4 text-[#FFFFFF]">{customer.rmCode}</td>
                <td className="px-4 py-4">
                  <div className="text-[#FFFFFF] font-medium">{customer.products}</div>
                  <div className="text-xs text-[#B0B0B0]">products</div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-[#B4E50D] font-bold">${customer.totalBalance.toLocaleString()}</div>
                  <div className="text-xs text-[#B0B0B0]">${customer.annualIncome.toLocaleString()}/year</div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                      <Visibility className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-[#2A2A2A] rounded text-[#B0B0B0] hover:text-[#FF6B6B] transition-colors">
                      <Delete className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-[#B0B0B0]">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;